/**
 * Sheet `duedate-complete-sheet` — „Erledigt" fragt **nicht** nach, sondern
 * nach dem Tag (Spec, „Aktionen"): `duedate-completed-on`, vorbelegt mit
 * heute (`todayInBerlin`, `lib/date.ts`). Ein eigenes, kleines Sheet statt
 * einer sofortigen Aktion wie `task-done-<id>` — anders als bei einer
 * Aufgabe verschiebt „Erledigt" hier bei Wiederholung das nächste `due_on`,
 * und der häufigste Fall ist ein Datum in der Vergangenheit („der
 * Schornsteinfeger war letzte Woche da").
 *
 * Die Zeile zeigt danach das neue `due_on` — nicht aus der Antwort dieses
 * Sheets (die wird verworfen), sondern weil `useCompleteDueDate` über
 * `keys.dueDates.all` invalidiert und die Liste neu lädt (AK-412/AK-423).
 */

import { useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { ApiError } from '@/lib/api'
import { todayInBerlin } from '@/lib/date'
import { useCompleteDueDate } from '@/features/schedule/api'
import type { DueDateRow } from '@/features/schedule/api'

interface DueDateCompleteSheetProps {
  /** `null` → geschlossen. Anders als `DueDateSheet` (das den Anlegen-Modus
   * kennt) öffnet dieses Sheet ausschließlich zum Erledigen eines
   * bestehenden Termins. */
  readonly dueDate: DueDateRow | null
  readonly onOpenChange: (open: boolean) => void
}

export function DueDateCompleteSheet({ dueDate, onOpenChange }: DueDateCompleteSheetProps) {
  const open = dueDate !== null
  const completeMutation = useCompleteDueDate()

  const [completedOn, setCompletedOn] = useState(todayInBerlin())
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Dieselbe Bauform wie `DueDateSheet`/`TaskSheet` — jedes Öffnen beginnt
  // frisch, während des Renderns statt in einem Effekt.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setCompletedOn(todayInBerlin())
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  async function submit() {
    if (dueDate === null || completeMutation.isPending) return
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      await completeMutation.mutateAsync({
        dueDateId: dueDate.id,
        ifMatch: String(dueDate.version),
        body: { completed_on: completedOn },
      })
      onOpenChange(false)
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit()
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onOpenChange(false)
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[400px] md:data-[side=right]:!max-w-[400px]"
      >
        <form
          data-testid="duedate-complete-sheet"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex h-full flex-col"
        >
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">Termin erledigt</SheetTitle>
            {dueDate !== null && <p className="text-label text-muted">{dueDate.title}</p>}
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 px-4">
            <FormField
              label="Erledigt am"
              testId="duedate-completed-on"
              apiField="completed_on"
              errorPrefix="duedate-complete"
              type="date"
              required
              value={completedOn}
              onChange={setCompletedOn}
              error={fieldError}
            />

            {dueDate?.notes !== null && dueDate?.notes !== undefined && dueDate.notes !== '' && (
              <div className="text-label text-muted flex flex-col gap-1">
                Notizen
                <p data-testid="duedate-complete-notes" className="text-body text-text whitespace-pre-wrap">
                  {dueDate.notes}
                </p>
              </div>
            )}
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="duedate-complete-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="duedate-complete-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="duedate-complete-confirm"
                disabled={completeMutation.isPending}
                aria-busy={completeMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Erledigt
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
